import React from "react";
import {
  BarChart3,
  Package,
  QrCode,
  Plus,
  Eye,
  Truck,
  Activity,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const OverviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t("overview.title")}
          </h1>
          <p className="text-gray-600">{t("overview.subtitle")}</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
          <Plus className="w-5 h-5 mr-2 text-blue-600" />
          {t("overview.quick_actions")}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            {
              title: t("overview.products"),
              description: t("overview.products_desc"),
              icon: Package,
              color: "bg-purple-600 hover:bg-purple-700",
              onClick: () => navigate("/business/products"),
            },
            {
              title: t("overview.batches"),
              description: t("overview.batches_desc"),
              icon: Truck,
              color: "bg-green-600 hover:bg-green-700",
              onClick: () => navigate("/business/batches"),
            },
            {
              title: t("overview.analytics"),
              description: t("overview.analytics_desc"),
              icon: BarChart3,
              color: "bg-blue-600 hover:bg-blue-700",
              onClick: () => navigate("/business/analytics"),
            },
            {
              title: t("overview.tags"),
              description: t("overview.tags_desc"),
              icon: QrCode,
              color: "bg-orange-600 hover:bg-orange-700",
              onClick: () => navigate("/business/tags"),
            },
          ].map((action, index) => (
            <button
              key={index}
              onClick={action.onClick}
              className={`p-6 rounded-xl ${action.color} text-white text-left hover:transform hover:scale-105 transition-all duration-200 shadow-lg`}
            >
              <action.icon className="w-8 h-8 mb-3" />
              <h3 className="font-medium text-base mb-1">{action.title}</h3>
              <p className="text-xs opacity-90">{action.description}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div
          onClick={() => navigate("/business/products")}
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer border-l-4 border-purple-500"
        >
          <Package className="w-8 h-8 text-purple-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("overview.products")}
          </h3>
          <p className="text-sm text-gray-600">
            {t("overview.products_card_desc")}
          </p>
          <div className="mt-4 flex items-center text-purple-600 text-sm font-medium">
            {t("overview.go_products")}
            <Eye className="w-4 h-4 ml-2" />
          </div>
        </div>

        <div
          onClick={() => navigate("/business/batches")}
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer border-l-4 border-green-500"
        >
          <Truck className="w-8 h-8 text-green-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("overview.batches")}
          </h3>
          <p className="text-sm text-gray-600">
            {t("overview.batches_card_desc")}
          </p>
          <div className="mt-4 flex items-center text-green-600 text-sm font-medium">
            {t("overview.go_batches")}
            <Eye className="w-4 h-4 ml-2" />
          </div>
        </div>

        <div
          onClick={() => navigate("/business/analytics")}
          className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow cursor-pointer border-l-4 border-blue-500"
        >
          <BarChart3 className="w-8 h-8 text-blue-600 mb-3" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {t("overview.analytics")}
          </h3>
          <p className="text-sm text-gray-600">
            {t("overview.analytics_card_desc")}
          </p>
          <div className="mt-4 flex items-center text-blue-600 text-sm font-medium">
            {t("overview.go_analytics")}
            <Eye className="w-4 h-4 ml-2" />
          </div>
        </div>
      </div>

      {/* Info Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
        <div className="flex items-start">
          <Activity className="w-6 h-6 text-blue-600 mr-3 mt-1" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {t("overview.welcome_title")}
            </h3>
            <p className="text-sm text-gray-600">
              {t("overview.welcome_text")}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OverviewPage;
